import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import DataTable, { Column } from '@repo/ui/data-table'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getEnrollmentEndOfJourneyReviews } from '../../../api/end-of-journey-reviews/enrollment/get-enrollment-end-of-journey-reviews'
import { EnrollmentReviewStay } from '../../../api/end-of-journey-reviews/enrollment/types'
import useTranslate from '../../../lib/hooks/use-translate'
import ReviewRatingPill, {
  ReviewLoadError
} from '../ols/centers/center-tab-views/center-end-of-stay-appreciations/review-rating-pill'

type Props = {
  enrollmentId: number
}

type Row = EnrollmentReviewStay & { id: number }

/** "Appréciation sur son séjour": the youth's end-of-stay ratings, one row per stay, back office only. */
const EnrollmentProfileEndOfStayAppreciations = ({ enrollmentId }: Props) => {
  const { t } = useTranslate()
  const { data, isPending, error } = useQuery({
    queryKey: ['enrollment-end-of-journey-reviews', enrollmentId],
    queryFn: () => getEnrollmentEndOfJourneyReviews(enrollmentId)
  })

  if (isPending) return <Loader center />
  if (error) return <ReviewLoadError error={error} />
  if (!data) return null

  const columns: Column<Row>[] = [
    {
      header: t('enrollment.end.of.stay.appreciations.all.questions'),
      accessorKey: 'label',
      cell: ({ row }) => {
        const stay = row.original
        return (
          <div className="flex flex-col gap-1 min-w-48 py-2.5">
            <Typography
              variant="label-semibold"
              color={stay.isCurrent ? tailwindColors.primary.DEFAULT : undefined}
            >
              {stay.label}
            </Typography>
            {stay.description && (
              <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
                {stay.description}
              </Typography>
            )}
            <div className="flex flex-wrap gap-1">
              {stay.departureDate && stay.returnDate && (
                <Badge
                  text={DateFormatter.getDateRangeDDMMYYYY(stay.departureDate, stay.returnDate)}
                  className="whitespace-nowrap"
                />
              )}
              {stay.centerName && <Badge variant="blue" text={stay.centerName} />}
            </div>
          </div>
        )
      }
    },
    ...data.columns.map(
      ({ code, shortLabel }): Column<Row> => ({
        header: shortLabel,
        accessorKey: `ratings.${code}` as Column<Row>['accessorKey'],
        cell: ({ row }) => {
          const rating = row.original.ratings[code]
          return <ReviewRatingPill text={rating && `${rating.rating}/${rating.scaleMax}`} />
        }
      })
    )
  ]

  return (
    <DataTable<Row>
      manualSorting={false}
      columns={columns}
      data={data.stays.map((stay) => ({ ...stay, id: stay.enrollmentId }))}
      rowCount={data.stays.length}
      paginated={false}
      noResults={t('enrollment.end.of.stay.appreciations.no.stay')}
    />
  )
}

export default EnrollmentProfileEndOfStayAppreciations
